import { useCallback } from 'react';

import {
  UseForwardToPhoneNumberParams,
  UseForwardToPhoneNumberReturn,
} from './useForwardToPhoneNumber.decl';

import { ApolloCache } from '@apollo/client';
import {
  AddExternalNumberMutation,
  AddExternalNumberMutationVariables,
} from '@generated/AddExternalNumberMutation';
import { ADD_EXTERNAL_NUMBER_MUTATION } from '@graphql/mutations/AddExternalNumberMutation';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';

export function useForwardToPhoneNumber({
  user,
  hideInlineForm,
}: UseForwardToPhoneNumberParams): UseForwardToPhoneNumberReturn {
  const phoneNumbers = user.phoneNumbers ?? [];
  const [mutate] = useGraphMutationWithFeedback<
    AddExternalNumberMutation,
    AddExternalNumberMutationVariables
  >(ADD_EXTERNAL_NUMBER_MUTATION, {
    type: 'form_snackbar',
    validationErrorAsFormError: true,
  });

  const addPhoneNumber = useCallback(
    async ({ phoneNumber }: { phoneNumber: string }) => {
      const { error } = await mutate({
        variables: {
          agentID: user.ID,
          input: {
            isEnabled: true,
            phoneNumber,
          },
        },
        update: (cache: ApolloCache<AddExternalNumberMutation>, { data }) => {
          cache.modify({
            id: cache.identify({ __typename: 'AgentV2', ID: user.ID }),
            fields: {
              phoneNumbers(phoneNumbersRef, { toReference }) {
                /* istanbul ignore next */
                if (!data) {
                  return null;
                }

                const {
                  addExternalNumber: { digitConfirmationDisabled, ID, isEnabled },
                } = data;

                const newPhoneNumber = {
                  __typename: 'AgentPhoneNumber',
                  digitConfirmationDisabled,
                  ID,
                  isEnabled,
                  phoneNumber: phoneNumber.replace('+', ''),
                };

                return [...phoneNumbersRef, toReference(newPhoneNumber)];
              },
            },
          });
        },
      });

      hideInlineForm();

      return error;
    },
    [hideInlineForm, mutate, user.ID]
  );

  return {
    phoneNumbers,
    addPhoneNumber,
  };
}
